import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import CountryFlagCircle from 'components/CountryFlag/CountryFlagCircle'
import RightSideModal from 'components/RightSideModal/RightSideModal'
import newStatusIcon from 'img/icons/status/report-calendar-new.svg'
import pendingStatusIcon from 'img/icons/status/report-calendar-pending.svg'
import submittedStatusIcon from 'img/icons/status/report-calendar-submitted.svg'
import {
	canViewReportCalendarReturn,
	getReportCalendarReturnNavigation,
} from './reportCalendarReportDetails'
import { REPORT_CALENDAR_REPORT_DETAILS_DRAWER } from './reportCalendarReportDetailsConfig'
import '../styles/report-calendar-report-details-drawer.sass'

const PROGRESS_STEP_ICONS = {
	new: newStatusIcon,
	pending: pendingStatusIcon,
	submitted: submittedStatusIcon,
}

const ReportDetailsMetaRow = ({ label, value }) => (
	<div className='reporting-calendar-report-details-drawer__meta-row'>
		<span className='reporting-calendar-report-details-drawer__meta-label'>{label}</span>
		<span className='reporting-calendar-report-details-drawer__meta-value'>{value}</span>
	</div>
)

const ReportDetailsProgressIcon = ({ step }) => {
	const stepKey = String(step.key || step.id || '').toLowerCase()

	if (step.status === 'loading' || stepKey === 'loading') {
		return (
			<span
				className='reporting-calendar-report-details-drawer__progress-icon reporting-calendar-report-details-drawer__progress-icon--loading'
				aria-hidden='true'
			>
				<span className='reporting-calendar-report-details-drawer__progress-loader' />
			</span>
		)
	}

	const icon = PROGRESS_STEP_ICONS[stepKey] || pendingStatusIcon

	return (
		<span className='reporting-calendar-report-details-drawer__progress-icon reporting-calendar-report-details-drawer__progress-icon--typed'>
			<img src={icon} alt='' />
		</span>
	)
}

const ReportCalendarReportDetailsDrawer = ({
	open,
	report,
	onClose,
}) => {
	const navigate = useNavigate()
	const openProjectName = useSelector((state) => state.user?.openProjectName)

	if (!report) {
		return null
	}

	const canViewReturn = canViewReportCalendarReturn(report.source || report)
	const returnActionLabel = canViewReturn
		? REPORT_CALENDAR_REPORT_DETAILS_DRAWER.viewReturnLabel
		: REPORT_CALENDAR_REPORT_DETAILS_DRAWER.fillReturnLabel

	const handleReturnAction = () => {
		const { pathname, state } = getReportCalendarReturnNavigation(
			report.source || report,
			openProjectName,
		)
		navigate(pathname, state ? { state } : undefined)
	}

	const footer = (
		<div className='reporting-calendar-report-details-drawer__footer'>
			<button
				type='button'
				className='reporting-calendar-report-details-drawer__submit-btn'
				onClick={handleReturnAction}
			>
				{returnActionLabel}
			</button>
		</div>
	)

	return (
		<RightSideModal
			open={open}
			onClose={onClose}
			title={report.company}
			width={REPORT_CALENDAR_REPORT_DETAILS_DRAWER.width}
			className='RightSideModal--reportCalendarReportDetails'
			footer={footer}
			content={(
				<div className='reporting-calendar-report-details-drawer'>
					<div className='reporting-calendar-report-details-drawer__summary'>
						<CountryFlagCircle
							countryCode={report.jurisdictionCode}
							size={40}
						/>
						<div className='reporting-calendar-report-details-drawer__summary-text'>
							<span className='reporting-calendar-report-details-drawer__jurisdiction'>
								{report.jurisdictionName}
							</span>
							<span className='reporting-calendar-report-details-drawer__report-type'>
								{report.reportType}
							</span>
						</div>
					</div>

					<div className='reporting-calendar-report-details-drawer__meta'>
						<ReportDetailsMetaRow label='Due date' value={report.dueDateLabel} />
						<ReportDetailsMetaRow label='Frequency' value={report.frequency} />
						<ReportDetailsMetaRow label='Period' value={report.period} />
					</div>

					{report.progressSteps?.length > 0 ? (
						<div className='reporting-calendar-report-details-drawer__progress'>
							<h3 className='reporting-calendar-report-details-drawer__progress-title'>Progress</h3>
							<ul className='reporting-calendar-report-details-drawer__progress-list'>
								{report.progressSteps.map((step) => (
									<li
										key={step.id}
										className='reporting-calendar-report-details-drawer__progress-item'
									>
										<ReportDetailsProgressIcon step={step} />
										<span className='reporting-calendar-report-details-drawer__progress-label'>
											{step.label}
										</span>
										<span className='reporting-calendar-report-details-drawer__progress-date'>
											{step.changedAtLabel || '—'}
										</span>
									</li>
								))}
							</ul>
						</div>
					) : null}
				</div>
			)}
		/>
	)
}

export default ReportCalendarReportDetailsDrawer
